import React, { createContext, FC, ReactNode, useContext, useEffect, useState } from 'react';

type FilterContextValue<T> = {
  state: T;
  setState: (patch: Partial<T>) => void;
  isActive: boolean;
  reset: () => void;
};

type CreateFilterContextConfig<T> = {
  storageKey: string;
  defaults: T;
  isActive: (state: T) => boolean;
};

export const createFilterContext = <T extends object>(config: CreateFilterContextConfig<T>) => {
  const { storageKey, defaults, isActive } = config;

  const loadFromStorage = (): Partial<T> => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as Partial<T>) : {};
    } catch {
      return {};
    }
  };

  const saveToStorage = (state: T) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // ignore
    }
  };

  const Context = createContext<FilterContextValue<T>>({
    state: defaults,
    setState: () => {},
    isActive: false,
    reset: () => {},
  });

  const Provider: FC<{ children: ReactNode }> = ({ children }) => {
    const [state, setStateObj] = useState<T>(() => ({ ...defaults, ...loadFromStorage() }));

    const setState = (patch: Partial<T>) => {
      setStateObj((prev) => ({ ...prev, ...patch }));
    };

    const reset = () => {
      setStateObj(defaults);
    };

    useEffect(() => {
      saveToStorage(state);
    }, [state]);

    return (
      <Context.Provider value={{ state, setState, isActive: isActive(state), reset }}>{children}</Context.Provider>
    );
  };

  const useBaseFilter = () => useContext(Context);

  return { Provider, useBaseFilter };
};
